import React, { useEffect, useState } from 'react';

// COMPONENTS
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import { AreaButton } from '@/main/components/shared/styles';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';

// REDUX/UTILS/HOOKS
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { KeyValue } from '@/main/models/common';
import { resetReducerCompany, useCompanyReducer } from '@/main/store/company';
import { userIsInsurer, userIsSupplier } from '@/main/services/utilsUser';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { InsurerFilterRequest } from '@/main/models/request';

import * as S from './styles';
import {
  getSystemApplication,
  useSystemApplicationReducer
} from '@/main/store/systemApplication';
import { useInsurerReducer } from '@/main/store/insurer/insurerHooks';
import { getInsurers, resetReducerInsurers } from '@/main/store/insurer';
import {
  getDomainBySystemApplication,
  useTemplateEmailNotificationReducer
} from '@/main/store/templateEmailNotification';
import { DomainBySystemApplicationRequest } from '@/main/models/request/templateEmailNotificationRequest';
import { SearchgridTemplateRequest } from '@/main/models/request/templateEmailNotificationCompanyRequest';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

interface IFilterEmailTemplates {
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataEmailTemplate: (filters: SearchgridTemplateRequest) => void;
}

function FiltersEmailTemplates({
  showFilters,
  setShowFilters,
  onResetSearch,
  getDataEmailTemplate
}: IFilterEmailTemplates) {
  //#region Hooks
  const dispatch = useDispatch();
  //#endregion

  //#region Reducers
  const { countriesUser: countryReducer } = useCountryReducer();
  const { company: companyReducer } = useCompanyReducer();
  const insurerReducer = useInsurerReducer();
  const systemApplicationReducer = useSystemApplicationReducer();
  const templateEmailNotificationReducer = useTemplateEmailNotificationReducer();
  //#endregion

  //#region States
  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );
  const optionDefaultCompanyDrop = getDefaultOptionDrop(companyReducer.data);
  const optionDefaultInsurerDrop = getDefaultOptionDrop(
    insurerReducer.insurers.data
  );

  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    insurer: null as KeyValue | null,
    systemApplication: null as KeyValue | null,
    templateType: null as KeyValue | null
  };
  type ISearchEmailTemplatesForm = typeof INITIAL_FORM_STATE;

  const methods = useFormQueryParams<ISearchEmailTemplatesForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer,
      insurer: insurerReducer.insurers,
      systemApplication: systemApplicationReducer.systemApplication,
      templateType: templateEmailNotificationReducer.domainBySystemApplication
    },
    onSubmit: onSubmit
  });
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
    queryParamsValues,
    clearErrors,
    reset,
    customHandleSubmit
  } = methods;
  //#endregion

  //#region Core functions
  function handleClear() {
    onResetSearch();
    reset(INITIAL_FORM_STATE);
    setValue('country', optionDefaultCountryDrop);
    setValue('insurer', optionDefaultInsurerDrop)
    clearErrors();
  }

  function onSubmit(formInputs: ISearchEmailTemplatesForm) {
    const request: SearchgridTemplateRequest = {
      guidCountry: String(formInputs.country?.key),
      guidInsurer: String(formInputs.insurer?.key),
      idSystemApplication: !!formInputs.systemApplication ? Number(formInputs.systemApplication?.key) : undefined,
      idTemplateEmailNotification: !!formInputs.templateType ? Number(formInputs.templateType?.key) : undefined
    };

    getDataEmailTemplate(removeEmptyValues(request));
    setShowFilters(false);
  }
  //#endregion

  // #region Async functions
  function callInsurerApiDomain(optionSelected: KeyValue | null) {
    setValue('insurer', null);

    if (optionSelected !== null) {
      const request: InsurerFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters,
        idStatusRegister: StatusRegisterEnum.Active,
      };
      dispatch(getInsurers(request));
    } else {
      dispatch(resetReducerInsurers());
    }
  }

  function callDomainBySystemApplicationApi(optionSelected: KeyValue | null) {
    setValue('templateType', null);

    if (optionSelected !== null) {
      const request: DomainBySystemApplicationRequest = {
        idSystemApplication: Number(optionSelected.key)
      };
      dispatch(getDomainBySystemApplication(request));
    } else {
      dispatch(getDomainBySystemApplication());
    }
  }
  //#endregion

  //#region UseEffects
  useEffect(() => {
    dispatch(getSystemApplication());
    dispatch(getDomainBySystemApplication());
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());

    return () => {
      dispatch(resetReducerCompany());
      dispatch(resetReducerInsurers());
    };
  }, []);

  useEffect(() => {
    if (optionDefaultCountryDrop) {
      callInsurerApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop]);
  //#endregion

  //#region Render constants
  const ID_SCREEN = 'id-searchEmailTemplates';
  const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;
  const hasLoadingReducers = countryReducer.isLoading;

  const isDisabledInsurerDrop = () =>
    insurerReducer.insurers.data === null ||
    userIsInsurer() ||
    getValues().country === null ||
    optionDefaultCompanyDrop !== null;
  //#endregion

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.RowInputs>
          <KogAutoCompleteControlled
            required
            id={generateIdScreen('select-country')}
            nameCustom="country"
            control={control}
            label={getTranslateByKey('information.country')}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            rulesCustom={{ required: true }}
            errorMessageCustom={
              errors.country?.type === 'required'
                ? getTranslateByKey('information.countryRequired', true)
                : ''
            }
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callInsurerApiDomain(optionSelected);
            }}
            hasErrorCustom={!!errors.country}
            valueDefaultKey={optionDefaultCountryDrop?.key}
            setValueCustom={setValue}
            disabled={optionDefaultCountryDrop !== null}
          />

          <KogAutoCompleteControlled
            id={generateIdScreen('select-insurer')}
            label={getTranslateByKey('information.insurer')}
            isDefaultValueUnique
            control={control}
            nameCustom="insurer"
            rulesCustom={{ required: false }}
            errorMessageCustom=""
            hasErrorCustom={false}
            options={insurerReducer.insurers.data}
            isLoading={insurerReducer.insurers.isLoading}
            onChangeCustom={() => { }}
            disabled={isDisabledInsurerDrop()}
            autoHighlightCustom={!!optionDefaultInsurerDrop}
            setValueCustom={setValue}
          />
          <KogAutoCompleteControlled
            id={generateIdScreen('select-systemApplication')}
            label={getTranslateByKey('information.systemApplication')}
            control={control}
            nameCustom="systemApplication"
            rulesCustom={{ required: false }}
            errorMessageCustom=""
            hasErrorCustom={false}
            options={systemApplicationReducer.systemApplication.data}
            isLoading={systemApplicationReducer.systemApplication.isLoading}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callDomainBySystemApplicationApi(optionSelected);
            }}
            disabled={false}
            setValueCustom={setValue}
          />
          <KogAutoCompleteControlled
            id={generateIdScreen('select-templateType')}
            label={getTranslateByKey('information.templateType')}
            control={control}
            nameCustom="templateType"
            rulesCustom={{ required: false }}
            errorMessageCustom=""
            hasErrorCustom={false}
            options={
              templateEmailNotificationReducer.domainBySystemApplication.data
            }
            isLoading={
              templateEmailNotificationReducer.domainBySystemApplication
                .isLoading
            }
            onChangeCustom={() => { }}
            disabled={false}
            setValueCustom={setValue}
          />
        </S.RowInputs>

        <S.AreaButtons>
          <SecondaryButton
            id={generateIdScreen('btn-submit')}
            disabled={hasLoadingReducers}
            type="submit">
            {getTranslateByKey('information.search')}
          </SecondaryButton>
          <SecondaryButton
            id={generateIdScreen('btn-clear')}
            type="button"
            onClick={handleClear}>
            {getTranslateByKey('information.clear')}
          </SecondaryButton>
        </S.AreaButtons>
      </form>
    </Box>
  );
}

function FiltersEmailTemplatesWrapper(
  props: Omit<IFilterEmailTemplates, 'showFilters' | 'setShowFilters'>
) {
  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  //#endregion

  //#region hooks
  const navigate = useNavigate();
  //#endregion

  //#region HandleFunctions
  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  //#endregion

  const mergedProps: IFilterEmailTemplates = {
    ...props,
    showFilters,
    setShowFilters
  };

  return (
    <>
      <AreaButton>
        <Button
          onClick={handleFilters}
          id="id-searchEmailTemplates-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
      </AreaButton>
      <FiltersEmailTemplates {...mergedProps} />
    </>
  );
}

export default FiltersEmailTemplatesWrapper;
